import { customFetch } from '@agos/api-client'
import {
  TICKET_STATUSES,
  isTicketOutstanding,
  type TicketCategory,
  type TicketPriority,
  type TicketStatus,
} from '@agos/shared'

/**
 * The platform's ticket queue - `SAAS.md` §3.137, `BACKEND.md` §10A.267.
 *
 * **`customFetch` with declared types**, on §9.513's self-clearing exemption: the endpoints post-date the exported
 * spec, so no generated hook exists yet, and a test asserts these paths are absent from the committed spec so
 * regenerating the client forces the move to hooks.
 */

export interface QueuedTicket {
  id: number
  franchise_id: number
  /** The endpoint joins it, so a row says which restaurant without a second call. */
  franchise_name: string
  subject: string
  category: TicketCategory
  priority: TicketPriority
  status: TicketStatus
  raised_by: string | null
  created_at: string
  updated_at: string
}

export interface QueuedMessage {
  id: number
  body: string
  author_side: string
  author_name: string | null
  created_at: string
}

export interface QueuedThread extends QueuedTicket {
  messages: QueuedMessage[]
}

/** Empty means every status; the server checks the value against its own list. */
export async function fetchQueue(status: string): Promise<QueuedTicket[]> {
  const query = status === '' ? '' : `?status=${encodeURIComponent(status)}`
  return customFetch<QueuedTicket[]>(`/api/v1/super-admin/support-tickets${query}`)
}

export async function fetchQueuedThread(ticketId: number): Promise<QueuedThread> {
  return customFetch<QueuedThread>(`/api/v1/super-admin/support-tickets/${String(ticketId)}`)
}

export async function answerTicket(ticketId: number, body: string): Promise<QueuedThread> {
  return customFetch<QueuedThread>(
    `/api/v1/super-admin/support-tickets/${String(ticketId)}/messages`,
    { method: 'POST', body: JSON.stringify({ body }) },
  )
}

export async function triageTicket(
  ticketId: number,
  change: { status?: TicketStatus; priority?: TicketPriority },
): Promise<QueuedThread> {
  return customFetch<QueuedThread>(`/api/v1/super-admin/support-tickets/${String(ticketId)}`, {
    method: 'PATCH',
    body: JSON.stringify(change),
  })
}

/**
 * The statuses this screen offers as buttons for a ticket that is currently `from`.
 *
 * **Derived from `TICKET_STATUSES` rather than written out**, so a sixth appears here without anybody remembering -
 * §3.133's defect is a control that looks derived and is not.
 *
 * **Nothing moves out of `closed`**, which is §10A.265's rule: a closed conversation is reopened by whoever ended
 * it, and the platform did not. Offering the button would draw one the server refuses.
 */
export function platformMayMoveTo(from: TicketStatus): readonly TicketStatus[] {
  if (from === 'closed') return []
  return TICKET_STATUSES.filter((status) => status !== from)
}

/**
 * How many of these are still work, for the heading.
 *
 * The same predicate the manager's badge uses, so the two sides cannot disagree about what *outstanding* means -
 * which would make the platform's count and the franchise's count two different numbers for one thing.
 */
export function outstandingInQueue(tickets: readonly QueuedTicket[]): number {
  return tickets.filter((ticket) => isTicketOutstanding(ticket.status)).length
}

/**
 * The default filter, which is the outstanding work rather than everything.
 *
 * A queue whose default view includes closed tickets is a queue nobody trusts after a month. `open` is the narrowest
 * honest default: it is where a new report lands, and the other two outstanding statuses are reachable by name.
 */
export const DEFAULT_QUEUE_FILTER: TicketStatus = 'open'

/**
 * Take a ticket off both queues, keeping it - §10A.273.
 *
 * The owner chose **both** when asked what removing should mean, so this is the recoverable half and the
 * one the queue offers by default. A thread is a record of what was promised to a franchise.
 */
export async function removeTicket(ticketId: number): Promise<{ removed: boolean }> {
  return customFetch<{ removed: boolean }>(
    `/api/v1/super-admin/support-tickets/${String(ticketId)}`,
    { method: 'DELETE' },
  )
}

/**
 * Destroy a ticket and its conversation - §10A.273, the spam case.
 *
 * **Its own function rather than a parameter on the one above**, so a call site cannot become destructive
 * by having a flag flipped: the two are different decisions and read differently at the point of use.
 */
export async function deleteTicketForGood(ticketId: number): Promise<{ deleted: boolean }> {
  return customFetch<{ deleted: boolean }>(
    `/api/v1/super-admin/support-tickets/${String(ticketId)}?purge=true`,
    { method: 'DELETE' },
  )
}

/**
 * How many tickets sit in each status - §3.140.
 *
 * The queue is read whole and filtered in the browser, so every filter can carry its own count: a triager
 * wants to know there are three urgent ones **before** clicking the filter that shows them.
 */
export function countByStatus(tickets: readonly QueuedTicket[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const ticket of tickets) {
    counts[ticket.status] = (counts[ticket.status] ?? 0) + 1
  }
  return counts
}

/** Whether a ticket belongs in the chosen filter. An empty filter is *all of them*, not *none*. */
export function matchesFilter(ticket: QueuedTicket, filter: string): boolean {
  return filter === '' || ticket.status === filter
}
